// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@adl/App/Components/Common/XngZone";
import bannerPageLayoutSharedStyles from "@adl/App/SharedStyle/BannerPageLayoutSharedStyles";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";

const enum fields {
    gap = "gap",
    image = "image",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.gap]: number;
        [fields.image]: string;
    };
}

interface BannerPageLayoutStyles {
    wrapper?: InjectableCss;
    bannerSection?: InjectableCss;
    bannerWrapper?: InjectableCss;
    infoSection?: InjectableCss;
    mainSection?: InjectableCss;
}

const bannerPageLayoutStyles: BannerPageLayoutStyles = {
    bannerSection: {
        ...bannerPageLayoutSharedStyles.bannerSection,
    },
    bannerWrapper: {
        ...bannerPageLayoutSharedStyles.bannerWrapper,
    },
    infoSection: {
        ...bannerPageLayoutSharedStyles.infoSection,
    },
    mainSection: {
        ...bannerPageLayoutSharedStyles.mainSection,
    },
};

const styles = bannerPageLayoutStyles;

export const BannerPageLayout: FC<OwnProps> = ({ id, fields }) => {
    const { isEditing } = useShellContext();
    const currentPageType = useAppSelector(state => getCurrentPage(state).type);
    const category = useContext(CategoryContext);
    const image = currentPageType === "CategoryDetailsPage" ? category?.properties?.bannerImageURL : fields.image;

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.bannerSection} {...{ gap: fields.gap }} data-test-selector="Banner">
                <StyledWrapper {...styles.bannerWrapper} {...{ backgroundImage: image, isCmsEditing: isEditing }}>
                    <StyledWrapper {...styles.infoSection}>
                        <PageBreadcrumbs />
                        <XngZone contentId={id} zoneName="Content00" hint="Title" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.mainSection} data-test-selector="MainSection">
                <XngZone contentId={id} zoneName="Content01" hint="Main Section" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: BannerPageLayout,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.gap,
                displayName: "Gap",
                editorTemplate: "IntegerField",
                defaultValue: 8,
                fieldType: "General",
            },
            {
                name: fields.image,
                displayName: "Background Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
